import { OFFICES, ORIENTATIONS, type Office, type Politician, type Region } from './types';

export type Verdict = 'correct' | 'partial' | 'wrong';
export type Arrow = 'up' | 'down' | 'left' | 'right';
export type AttrKey = 'gender' | 'party' | 'orientation' | 'office' | 'status' | 'region' | 'year';

export interface Cell {
  key: AttrKey;
  verdict: Verdict;
  /** The guessed politician's value, ready to show. */
  text: string;
  arrow?: Arrow;
}

export const COLUMNS: { key: AttrKey; label: string; short: string; help: string }[] = [
  { key: 'gender', short: 'Pohl.', label: 'Pohlavie', help: 'Muž alebo žena.' },
  {
    key: 'party',
    short: 'Strana',
    label: 'Strana',
    help: 'Súčasná strana (alebo posledná, ak už nie je v politike). Oranžová: hľadaný politik bol niekedy v rovnakej strane.',
  },
  {
    key: 'orientation',
    short: 'Orient.',
    label: 'Orientácia',
    help: 'Politické zaradenie strany podľa Wikipédie. Šípka ukazuje, ktorým smerom hľadať. Oranžová: susedná pozícia.',
  },
  {
    key: 'office',
    short: 'Funkcia',
    label: 'Najvyššia funkcia',
    help: 'Najvyššia funkcia, akú kedy zastával. Poradie: prezident › premiér › predseda NR SR › eurokomisár › minister › europoslanec › poslanec NR SR › iné.',
  },
  {
    key: 'status',
    short: 'Dnes',
    label: 'Dnes',
    help: 'Čo robí teraz: prezident, koalícia, opozícia, EÚ (europoslanec/eurokomisár), alebo mimo politiky.',
  },
  {
    key: 'region',
    short: 'Kraj',
    label: 'Kraj narodenia',
    help: 'Kraj, v ktorom sa narodil (podľa dnešného členenia). Oranžová: susedný kraj.',
  },
  { key: 'year', short: 'Rok', label: 'Rok narodenia', help: 'Šípka ukazuje, či je hľadaný politik mladší alebo starší. Oranžová: rozdiel do 3 rokov.' },
];

const REGION_NEIGHBOURS: Record<Region, Region[]> = {
  Bratislavský: ['Trnavský'],
  Trnavský: ['Bratislavský', 'Trenčiansky', 'Nitriansky'],
  Trenčiansky: ['Trnavský', 'Nitriansky', 'Žilinský', 'Banskobystrický'],
  Nitriansky: ['Trnavský', 'Trenčiansky', 'Banskobystrický'],
  Žilinský: ['Trenčiansky', 'Banskobystrický', 'Prešovský'],
  Banskobystrický: ['Trenčiansky', 'Nitriansky', 'Žilinský', 'Prešovský', 'Košický'],
  Prešovský: ['Žilinský', 'Banskobystrický', 'Košický'],
  Košický: ['Banskobystrický', 'Prešovský'],
  Zahraničie: [],
};

const NONPARTISAN = 'Nestraník';

const FEMININE_OFFICE: Record<Office, string> = {
  Prezident: 'Prezidentka',
  Premiér: 'Premiérka',
  'Predseda NR SR': 'Predsedníčka NR SR',
  Eurokomisár: 'Eurokomisárka',
  Minister: 'Ministerka',
  Europoslanec: 'Europoslankyňa',
  'Poslanec NR SR': 'Poslankyňa NR SR',
  Iné: 'Iné',
};

export function birthYear(p: Politician): number {
  return Number(p.born.slice(0, 4));
}

export function officeLabel(p: Politician): string {
  return p.gender === 'F' ? FEMININE_OFFICE[p.office] : p.office;
}

export function partyLabel(p: Politician): string {
  return p.party === NONPARTISAN && p.gender === 'F' ? 'Nestraníčka' : p.party;
}

export function statusLabel(p: Politician): string {
  return p.status === 'Prezident' && p.gender === 'F' ? 'Prezidentka' : p.status;
}

export function genderLabel(p: Politician): string {
  return p.gender === 'F' ? 'Žena' : 'Muž';
}

function compareParty(guess: Politician, target: Politician): Verdict {
  if (guess.party === target.party) return 'correct';
  const past = new Set(target.partyHistory.filter((x) => x !== NONPARTISAN));
  return guess.partyHistory.some((x) => past.has(x)) ? 'partial' : 'wrong';
}

export function compare(guess: Politician, target: Politician): Cell[] {
  const cells: Cell[] = [];

  cells.push({
    key: 'gender',
    verdict: guess.gender === target.gender ? 'correct' : 'wrong',
    text: genderLabel(guess),
  });

  cells.push({ key: 'party', verdict: compareParty(guess, target), text: partyLabel(guess) });

  const go = ORIENTATIONS.indexOf(guess.orientation);
  const to = ORIENTATIONS.indexOf(target.orientation);
  cells.push({
    key: 'orientation',
    verdict: go === to ? 'correct' : Math.abs(go - to) === 1 ? 'partial' : 'wrong',
    text: guess.orientation,
    arrow: go === to ? undefined : to > go ? 'right' : 'left',
  });

  const gOffice = OFFICES.indexOf(guess.office);
  const tOffice = OFFICES.indexOf(target.office);
  cells.push({
    key: 'office',
    verdict: gOffice === tOffice ? 'correct' : 'wrong',
    text: officeLabel(guess),
    // Lower index = higher office.
    arrow: gOffice === tOffice ? undefined : tOffice < gOffice ? 'up' : 'down',
  });

  cells.push({
    key: 'status',
    verdict: guess.status === target.status ? 'correct' : 'wrong',
    text: statusLabel(guess),
  });

  cells.push({
    key: 'region',
    verdict:
      guess.region === target.region
        ? 'correct'
        : REGION_NEIGHBOURS[target.region].includes(guess.region)
          ? 'partial'
          : 'wrong',
    text: guess.region,
  });

  const gy = birthYear(guess);
  const ty = birthYear(target);
  cells.push({
    key: 'year',
    verdict: gy === ty ? 'correct' : Math.abs(gy - ty) <= 3 ? 'partial' : 'wrong',
    text: String(gy),
    arrow: gy === ty ? undefined : ty > gy ? 'up' : 'down',
  });

  return cells;
}
